import React from 'react';
import { Activity, Utensils, Dumbbell, Droplets, Brain, BookOpen } from 'lucide-react';

const outerFeatures = [
  {
    id: 1,
    title: "Glucose",
    description: "Blood sugar tracking",
    icon: Activity,
    position: "top"
  },
  {
    id: 2,
    title: "Nutrition",
    description: "Meal & calorie logging",
    icon: Utensils,
    position: "top-right"
  },
  {
    id: 3,
    title: "Fitness",
    description: "Activity tracking",
    icon: Dumbbell,
    position: "bottom-right"
  },
  {
    id: 4,
    title: "Hydration",
    description: "Water intake",
    icon: Droplets,
    position: "bottom-left"
  },
  {
    id: 5,
    title: "Mental Health",
    description: "Mood & wellness",
    icon: Brain,
    position: "top-left"
  }
];

const Ecosystem = () => {
  return (
    <section className="py-24 bg-white relative overflow-hidden">
      {/* Subtle background decoration */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-teal-50/50 rounded-full blur-3xl" />
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-cyan-50/50 rounded-full blur-3xl" />

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        {/* Section header */}
        <div className="text-center mb-12">
          <h2 className="text-4xl lg:text-5xl font-bold text-slate-800 mb-6">
            Here's Where Your Diabetes
            <br />
            <span className="bg-gradient-to-r from-teal-600 to-cyan-600 bg-clip-text text-transparent">
              Story Comes Together
            </span>
          </h2>
        </div>

        {/* Main description card */}
        <div className="max-w-4xl mx-auto mb-10">
          <div className="bg-gradient-to-br from-slate-50 to-teal-50/30 rounded-3xl p-8 lg:p-10 border border-slate-100 shadow-sm">
            <p className="text-lg lg:text-xl text-slate-700 leading-relaxed text-center">
              A1C Survival Plan brings glucose, nutrition, fitness, hydration, mental health, and daily life into one connected journal — helping you see how your choices interact, not just how they're logged.
            </p>
          </div>
        </div>

        {/* Circular layout container */}
        <div className="relative max-w-4xl mx-auto">
          {/* SVG for connecting lines */}
          <svg 
            className="absolute inset-0 w-full h-full pointer-events-none"
            viewBox="0 0 800 600"
            preserveAspectRatio="xMidYMid meet"
          >
            {/* Connecting lines from outer cards to center */}
            {/* Top */}
            <line x1="400" y1="80" x2="400" y2="240" stroke="url(#lineGradient)" strokeWidth="2" strokeDasharray="6 4" opacity="0.4" />
            {/* Top Right */}
            <line x1="620" y1="150" x2="480" y2="260" stroke="url(#lineGradient)" strokeWidth="2" strokeDasharray="6 4" opacity="0.4" />
            {/* Bottom Right */}
            <line x1="620" y1="450" x2="480" y2="340" stroke="url(#lineGradient)" strokeWidth="2" strokeDasharray="6 4" opacity="0.4" />
            {/* Bottom Left */}
            <line x1="180" y1="450" x2="320" y2="340" stroke="url(#lineGradient)" strokeWidth="2" strokeDasharray="6 4" opacity="0.4" />
            {/* Top Left */}
            <line x1="180" y1="150" x2="320" y2="260" stroke="url(#lineGradient)" strokeWidth="2" strokeDasharray="6 4" opacity="0.4" />
            
            {/* Gradient definition */}
            <defs>
              <linearGradient id="lineGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#14b8a6" />
                <stop offset="100%" stopColor="#06b6d4" />
              </linearGradient>
            </defs>
          </svg>

          {/* Circle layout */}
          <div className="relative h-[600px] md:h-[550px]">
            
            {/* Center - Journal */}
            <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-20">
              <div className="group bg-gradient-to-br from-teal-500 to-cyan-500 rounded-3xl p-6 md:p-8 shadow-2xl shadow-teal-200/50 w-[180px] md:w-[200px] text-center hover:scale-105 transition-transform duration-300">
                <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <BookOpen className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2">Journal</h3>
                <p className="text-white/80 text-sm">Your unified health story</p>
              </div>
            </div>

            {/* Top - Glucose */}
            <div className="absolute left-1/2 -translate-x-1/2 top-0">
              <FeatureCard feature={outerFeatures[0]} />
            </div>

            {/* Top Right - Nutrition */}
            <div className="absolute right-0 md:right-4 top-[15%]">
              <FeatureCard feature={outerFeatures[1]} />
            </div>

            {/* Bottom Right - Fitness */}
            <div className="absolute right-0 md:right-4 bottom-[15%]">
              <FeatureCard feature={outerFeatures[2]} />
            </div>

            {/* Bottom Left - Hydration */}
            <div className="absolute left-0 md:left-4 bottom-[15%]">
              <FeatureCard feature={outerFeatures[3]} />
            </div>

            {/* Top Left - Mental Health */}
            <div className="absolute left-0 md:left-4 top-[15%]">
              <FeatureCard feature={outerFeatures[4]} />
            </div>

          </div>
        </div>

        {/* Bottom description card */}
        <div className="max-w-4xl mx-auto mt-10">
          <div className="bg-gradient-to-br from-slate-50 to-teal-50/30 rounded-3xl p-8 lg:p-10 border border-slate-100 shadow-sm">
            <p className="text-lg lg:text-xl text-slate-700 leading-relaxed text-center">
              Most diabetes apps track isolated data points.
              <br /><br />
              <span className="font-semibold text-slate-800">A1C Survival Plan connects them.</span>
              <br /><br />
              At the center is your Journal — a place to document meals, glucose, activity, hydration, mood, and daily context. Each entry feeds into a broader picture, revealing patterns over time and turning everyday inputs into meaningful signals.
              <br /><br />
              The result isn't more data — it's greater awareness, consistency, and long-term strength.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

const FeatureCard = ({ feature }) => {
  const IconComponent = feature.icon;
  return (
    <div className="group bg-white rounded-2xl p-4 md:p-5 border border-slate-100 shadow-md hover:shadow-xl hover:border-teal-200 transition-all duration-300 w-[140px] md:w-[160px] text-center hover:scale-105">
      <div className="w-12 h-12 bg-gradient-to-br from-teal-100 to-cyan-100 rounded-xl flex items-center justify-center mx-auto mb-3 group-hover:from-teal-500 group-hover:to-cyan-500 transition-all duration-300">
        <IconComponent className="w-6 h-6 text-teal-600 group-hover:text-white transition-colors duration-300" />
      </div>
      <h3 className="text-base font-bold text-slate-800 mb-1">{feature.title}</h3>
      <p className="text-slate-500 text-xs">{feature.description}</p>
    </div>
  );
};

export default Ecosystem;
